import { useTheme } from "~/components/providers/Theme"
import { X } from "lucide-react"
import { contrastRatio } from "~/lib/theme"
import { cn } from "~/lib/utils"
import { hexToRgb, hslToRgb, rgbToHex, rgbToHsl } from "~/lib/color-utils"
import { Button } from "../ui/button"

export function IssueLabel({
	label,
	truncateNameTo = 100,
	onRemove,
	size = "default"
}: {
	label: { id: string; name: string; color: string }
	truncateNameTo?: number
	onRemove?: (labelId: string) => void
	size?: "default" | "compact" | "stacked"
}) {
	const { colorScheme } = useTheme()
	const dark = colorScheme === "dark"
	const hsl = rgbToHsl(hexToRgb(label.color) ?? { r: 99, g: 102, b: 241 })
	// The selected colour is a palette seed, not a solid fill. Keep the hue
	// throughout, like mentions: muted surface, colourful text and outline.
	const background = rgbToHex(
		hslToRgb({ ...hsl, s: hsl.s * 0.45, l: dark ? 22 : 94 })
	)
	let lightness = dark ? 76 : 34
	let foreground = rgbToHex(hslToRgb({ ...hsl, l: lightness }))
	while (
		contrastRatio(background, foreground) < 4.5 &&
		lightness > 0 &&
		lightness < 100
	) {
		lightness += dark ? 1 : -1
		foreground = rgbToHex(hslToRgb({ ...hsl, l: lightness }))
	}
	const border = rgbToHex(
		hslToRgb({ ...hsl, s: hsl.s * 0.7, l: dark ? 48 : 70 })
	)
	const truncatedName = `${truncateNameTo ? label.name.substring(0, truncateNameTo) : label.name}${label.name.length > truncateNameTo ? "..." : ""}`
	return (
		<span
			className={cn(
				"inline-flex min-w-0 items-center justify-center gap-2 rounded-full",
				size === "stacked"
					? "size-5 shrink-0 p-0"
					: size === "compact"
						? "max-w-24 px-1.5 py-0.5 text-[11px]"
						: "h-7 px-2 py-1.5 text-xs",
				"border"
			)}
			style={{
				backgroundColor: background,
				color: foreground,
				borderColor: border
			}}
		>
			<span
				className={cn(
					"truncate text-center font-semibold",
					size === "stacked" && "sr-only"
				)}
			>
				{truncatedName}
			</span>
			{onRemove && (
				<Button
					type="button"
					size="icon-2xs"
					variant="none"
					shape="pill"
					aria-label={`Remove ${label.name}`}
					onClick={() => onRemove(label.id)}
				>
					<X />
				</Button>
			)}
		</span>
	)
}
